// requestContext — the one place a non-Redux caller can learn who is asking.
//
// WHY THIS EXISTS. Two data paths reach the backend and only one of them can see Redux:
//
//   * RTK Query slices go through `apiSlice.prepareHeaders`, which reads the store. Fine.
//   * `features/wells/store/corpusApi.ts` — the SPINE's own reader — deliberately does NOT, and its
//     reasoning is sound: `spineStore.ts` is a `useSyncExternalStore` module store that needs no
//     Provider, so its test suites run without one. Routing spine hydration through RTK Query would
//     drag a Redux Provider into all of them.
//
// The consequence nobody had drawn: that path sends **no `Authorization` header and no acting
// organization**, and there is nowhere to attach them. Today it does not matter — the Flask server
// checks neither. It matters the moment a real backend requires both, because the fix would
// otherwise be "make the spine depend on Redux", i.e. undo a deliberate design decision under time
// pressure, in someone else's code.
//
// So: a module-level context, set once by the auth layer, read by anything that cannot see the
// store. Same idiom as `spineStore` itself — no Provider, no React, no import cycle back into
// `features/`.
//
// ─── WHAT THIS IS NOT ─────────────────────────────────────────────────────────────────────────
// NOT a security boundary. A client that states which organization it is acting as is making a
// CLAIM, and the server must re-verify that claim against the user's real memberships on every
// request. The distinction the access model turns on:
//
//     SUBJECT — "which of MY memberships am I acting under?"  ← client selects, server verifies
//     OBJECT  — "which organization owns this row?"           ← server stamps, never accepted
//
// Sending an org header is the first half. It is never the second.

export interface RequestContext {
  /** Bearer token, or null when unauthenticated. */
  token: string | null;
  /**
   * Which organization the user is acting as for this session. A SELECTION among their own
   * memberships — see the note above. `null` when unselected or single-membership.
   */
  activeOrganizationId: string | null;
}

let current: RequestContext = { token: null, activeOrganizationId: null };

/** Called by the auth layer on login, logout, token refresh, and organization switch. */
export function setRequestContext(next: Partial<RequestContext>): void {
  current = { ...current, ...next };
}

export function getRequestContext(): RequestContext {
  return current;
}

/** Test seam — a suite that authenticates must be able to put this back. */
export function clearRequestContext(): void {
  current = { token: null, activeOrganizationId: null };
}

/**
 * Headers for a non-RTK caller. Deliberately mirrors `apiSlice.prepareHeaders` so the two paths
 * present the SAME request shape to the backend — a backend engineer implementing one endpoint
 * should not discover that half the client sends different headers.
 *
 * BACKEND: `X-Organization-Id` states which membership the caller is acting under. Verify it
 * against the authenticated principal's memberships and reject if it is not one of them; never
 * trust it to select data ownership.
 */
export function authHeaders(): Record<string, string> {
  const h: Record<string, string> = {};
  if (current.token) h["Authorization"] = `Bearer ${current.token}`;
  if (current.activeOrganizationId)
    h["X-Organization-Id"] = current.activeOrganizationId;
  // Matches apiSlice: correlates a request across the FE, the server log and the audit record.
  h["X-Request-ID"] = crypto.randomUUID();
  return h;
}
